<template>
  <div
    :class="rootClasses"
    :data-testid="sectionTag"
    :role="rootRole"
    :aria-pressed="rootAriaPressed"
    :aria-disabled="rootAriaDisabled"
    :aria-labelledby="rootAriaLabelledBy"
    :aria-describedby="rootAriaDescribedBy"
    :tabindex="rootTabIndex"
    data-w-container-ignore-select
    @click="handleRootClick"
    @keydown.enter="handleRootKeyboardSelect"
    @keydown.space="handleRootKeyboardSelect"
  >
    <div class="w-selection-control__main">
      <WCheckbox
        v-if="variant === 'checkbox'"
        v-model="inputModel"
        class="w-selection-control__input"
        size="small"
        variant="no-padding"
        :aria-hidden="controlAriaHidden"
        :aria-labelledby="titleId"
        :aria-describedby="inputAriaDescribedBy"
        :disabled="disabled"
        :indeterminate="indeterminate"
        :name="name"
        :section-tag="sectionTag ? `${sectionTag}-checkbox` : undefined"
        :tabindex="effectiveControlTabIndex"
        :value="value"
        @click.stop
        @update:model-value="handleInputUpdate"
      />
      <WRadioBtn
        v-else-if="variant === 'radio'"
        v-model="inputModel"
        class="w-selection-control__input"
        variant="compact"
        :aria-hidden="controlAriaHidden"
        :aria-labelledby="titleId"
        :aria-describedby="inputAriaDescribedBy"
        :disabled="disabled"
        :name="name"
        :section-tag="sectionTag ? `${sectionTag}-radio` : undefined"
        :tabindex="effectiveControlTabIndex"
        :value="value"
        @click.stop
        @update:model-value="handleInputUpdate"
      />
      <div :id="titleId" :class="titleClasses">
        <slot name="title">{{ title }}</slot>
      </div>
    </div>

    <div v-if="hasSubtitle" class="w-selection-control__subtitle-row">
      <div :id="subtitleId" class="w-selection-control__subtitle">
        <slot name="subtitle">{{ subtitle }}</slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, useSlots } from 'vue'
import { tokens } from '@/plugins/vuetify'
import WCheckbox from './WCheckbox.vue'
import WRadioBtn from './WRadioBtn.vue'
import { getNextSelectionControlModel, isSelectionControlSelected } from './selectionControlModel'
import type {
  WSelectionControlModelValue,
  WSelectionControlSize,
  WSelectionControlValue,
  WSelectionControlVariant
} from './WSelectionControl.types'

export interface WSelectionControlProps {
  sectionTag?: string
  variant?: WSelectionControlVariant
  size?: WSelectionControlSize
  title?: string
  subtitle?: string
  disabled?: boolean
  indeterminate?: boolean
  name?: string
  value?: WSelectionControlValue
  selected?: boolean
  interactive?: boolean
  inputInteractive?: boolean
  controlTabIndex?: number
}

const props = withDefaults(defineProps<WSelectionControlProps>(), {
  sectionTag: 'unspecified',
  variant: 'checkbox',
  size: 'default',
  title: '',
  subtitle: undefined,
  disabled: false,
  indeterminate: false,
  name: undefined,
  value: undefined,
  selected: undefined,
  interactive: true,
  inputInteractive: true,
  controlTabIndex: undefined
})

const emit = defineEmits<{
  select: [value: WSelectionControlValue | undefined]
}>()

const model = defineModel<WSelectionControlModelValue>()
const slots = useSlots()
const instance = getCurrentInstance()
const componentId = `w-selection-control-${instance?.uid ?? 'fallback'}`
const titleId = `${componentId}-title`
const subtitleId = `${componentId}-subtitle`

const hasControl = computed(() => props.variant !== 'text')
const hasSubtitle = computed(() => Boolean(props.subtitle || slots.subtitle))
const hasInteractiveTextRole = computed(() => !hasControl.value && props.interactive)
const isSelected = computed(() => isSelectionControlSelected(model.value, props.value, props.selected))
const inputModel = computed<WSelectionControlModelValue>({
  get: () => getInputModel(),
  set: (value) => {
    if (props.inputInteractive) {
      model.value = value
    }
  }
})
const controlAriaDescribedBy = computed(() => (hasSubtitle.value ? subtitleId : undefined))
const inputAriaDescribedBy = computed(() => (props.inputInteractive ? controlAriaDescribedBy.value : undefined))
const controlAriaHidden = computed(() => (props.inputInteractive ? undefined : 'true'))
const effectiveControlTabIndex = computed(() => (props.inputInteractive ? props.controlTabIndex : -1))
const rootRole = computed(() => (hasInteractiveTextRole.value ? 'button' : undefined))
const rootAriaPressed = computed(() => (hasInteractiveTextRole.value ? isSelected.value : undefined))
const rootAriaDisabled = computed(() => (hasInteractiveTextRole.value && props.disabled ? 'true' : undefined))
const rootAriaLabelledBy = computed(() => (hasInteractiveTextRole.value ? titleId : undefined))
const rootAriaDescribedBy = computed(() => (hasInteractiveTextRole.value ? controlAriaDescribedBy.value : undefined))
const rootTabIndex = computed(() => (hasInteractiveTextRole.value && !props.disabled ? 0 : undefined))

const rootClasses = computed(() => ({
  'w-selection-control': true,
  [`w-selection-control--${props.variant}`]: true,
  [`w-selection-control--${props.size}`]: true,
  'w-selection-control--selected': isSelected.value,
  'w-selection-control--disabled': props.disabled,
  'w-selection-control--with-control': hasControl.value,
  'w-selection-control--with-subtitle': hasSubtitle.value,
  'w-selection-control--interactive': props.interactive && !props.disabled,
  'w-selection-control--input-presentational': !props.inputInteractive
}))

const titleClasses = computed(() => ({
  'w-selection-control__title': true,
  'w-selection-control__title--emphasis': hasSubtitle.value,
  'w-selection-control__title--regular': !hasSubtitle.value
}))

function getInputModel(): WSelectionControlModelValue {
  if (props.selected === undefined) {
    return model.value
  }

  if (props.selected) {
    if (Array.isArray(model.value) && props.value !== undefined) {
      return model.value.includes(props.value) ? model.value : [...model.value, props.value]
    }

    return props.value ?? true
  }

  if (Array.isArray(model.value)) {
    return props.value === undefined ? [] : model.value.filter((currentValue) => currentValue !== props.value)
  }

  if (props.value === undefined) {
    return false
  }

  return model.value === props.value ? null : model.value
}

function selectControl() {
  model.value = getNextSelectionControlModel(props.variant, model.value, props.value, isSelected.value)
  emit('select', props.value)
}

function handleRootClick() {
  if (!props.interactive || props.disabled) {
    return
  }

  selectControl()
}

function handleRootKeyboardSelect(event: KeyboardEvent) {
  if (hasControl.value || event.target !== event.currentTarget) {
    return
  }

  event.preventDefault()
  handleRootClick()
}

function handleInputUpdate() {
  if (!props.inputInteractive) {
    return
  }

  emit('select', props.value)
}
</script>

<style scoped lang="scss">
.w-selection-control {
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  min-width: 0;
  color: v-bind('tokens.color.content.text.primary');
}

.w-selection-control--loose {
  gap: v-bind('tokens.spacing.gap.icon');
}

.w-selection-control--interactive {
  cursor: pointer;
}

.w-selection-control--disabled {
  color: v-bind('tokens.color.content.text.muted');
  cursor: default;
}

.w-selection-control__main {
  display: flex;
  box-sizing: border-box;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 0;
}

.w-selection-control--with-control .w-selection-control__main {
  gap: v-bind('tokens.spacing.gap.small');
}

.w-selection-control__input {
  flex: 0 0 16px;
}

.w-selection-control--input-presentational .w-selection-control__input {
  pointer-events: none;
}

.w-selection-control__title {
  min-width: 0;
  flex: 1 1 auto;
  color: v-bind('tokens.color.content.text.primary');
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-word;
}

.w-selection-control__title--emphasis {
  @include tag(label-semibold-medium);
}

.w-selection-control__title--regular {
  @include tag(label-regular-medium);
}

.w-selection-control--disabled .w-selection-control__title {
  color: v-bind('tokens.color.content.text.muted');
}

.w-selection-control__subtitle-row {
  display: flex;
  box-sizing: border-box;
  align-items: center;
  width: 100%;
  min-width: 0;
}

.w-selection-control--with-control .w-selection-control__subtitle-row {
  padding-left: v-bind('tokens.spacing.inset.container.default');
}

.w-selection-control__subtitle {
  @include tag(label-regular-small);

  min-width: 0;
  flex: 1 1 auto;
  color: v-bind('tokens.color.content.text.muted');
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-word;
}

.w-selection-control--loose .w-selection-control__subtitle {
  @include tag(label-regular-medium);

  color: v-bind('tokens.color.content.text.secondary');
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
}

.w-selection-control--disabled .w-selection-control__subtitle {
  color: v-bind('tokens.color.content.text.muted');
}
</style>
